import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { doEncryptLocal, loog } from '@express-labs/raven-tools';
import TenderTypeModal from './TenderTypeModal';
import { HeaderDynamicContext } from '../../../../contexts/HeaderDynamicData/HeaderDynamicData';
import { analyze } from '../../../../helpers';
import { updateENCCPreferredModal, updatePreferredPayment } from '../../../../graphql/mutation';
import { getTenderTypePreferredCardHeaderModal, getPaymentMethods } from '../../../../queries';

function TenderTypeModalVM({
  deviceWidth,
  hideTopHeader,
  ...props
}) {
  const {
    customer,
    heartBeatData,
  } = useContext(HeaderDynamicContext);
  const { pathname } = window.location;

  const [isPrefCardModalOpen, setIsPrefCardModalOpen] = useState(true);


  const {
    loading: ttpLoading, error: ttpError, data: ttpData,
  } = useQuery(getTenderTypePreferredCardHeaderModal);
  const {
    loading: pmLoading, error: pmError, data: pmData,
  } = useQuery(getPaymentMethods);
  const [updateENCCPrefModal] = useMutation(updateENCCPreferredModal);
  const [updatePrefPayment] = useMutation(updatePreferredPayment, {
    refetchQueries: [
      { query: getPaymentMethods },
    ],
  });

  let savedNonPreferredENCC;

  if (pmData && !pmLoading && !pmError) {
    const payments = pmData?.paymentMethods?.payments;
    savedNonPreferredENCC = payments?.find((card) => (
      card.paymentType === 'ExpressCard' && !card.default
    ));
  }
  let ttpHeaderModalData;
  if (ttpData && !ttpLoading && !ttpError) {
    ttpHeaderModalData = ttpData?.getTenderTypePreferredCardHeaderModal;
  }

  function updateCustomerDataInStorage(customerData) {
    try {
      const hbeatStorage = localStorage?.getItem('exp_hbeat');
      if (hbeatStorage) {
        const hbeatData = JSON.parse(hbeatStorage);
        const { ct, iv, salt } = JSON.parse(doEncryptLocal.doEncrypt(
          `${hbeatData.anon ? 'A' : 'L'}${hbeatData.uid}`,
          JSON.stringify(customerData),
        ));
        sessionStorage?.setItem('hbSession', `${ct}|${iv}|${salt}`);
      }
    } catch (e) {
      /* istanbul ignore next */
      loog('Error while updating the customer data in storage', 'info', e.message);
    }
  }

  function handleDismiss() {
    updateENCCPrefModal()
      .then(() => {
        updateCustomerDataInStorage({
          ...heartBeatData,
          customerActedOnENCCAsPreferredModal: true,
        });
        window?.Heartbeat?.beat();
        setIsPrefCardModalOpen(false);
      })
      .catch((e) => {
        setIsPrefCardModalOpen(false);
        loog('Update ENCC preferred modal error', 'info', e.message);
      });
  }

  function handleSubmit() {
    updatePrefPayment({
      variables: {
        id: savedNonPreferredENCC?.paymentId,
      },
    })
      .then(() => {
        analyze({
          name: 'ttpHeaderModalAccepted',
          data: {
            url: window?.location?.href,
          },
          props: {},
        });
        handleDismiss();
      })
      .catch((e) => {
        setIsPrefCardModalOpen(false);
        loog('Update Preferred Payment error', 'info', e.message);
      });
  }

  const customerHasActedOnModal = (
    heartBeatData?.customer?.customerInfo?.customerActedOnENCCAsPreferredModal
  );

  const shouldShowPreferredCardModal = !!(
    ttpHeaderModalData?.enable // AEM flag should be true
    && customer?.loginStatus // customer should be logged in
    && !customerHasActedOnModal // customer flag is falsy
    && customerHasActedOnModal !== undefined // customer flag is defined
    && savedNonPreferredENCC // customer should have a saved ENCC that is not their preferred card
    && !pathname.toLowerCase().includes('/checkout') // should not show on checkout pages
    && ((deviceWidth === 'desktop' && !hideTopHeader) || deviceWidth === 'mobile')
  );

  useEffect(() => {
    if (shouldShowPreferredCardModal) {
      analyze({
        name: 'ttpHeaderModalDisplayed',
        data: {
          url: window?.location?.href,
        },
        props: {},
      });
    }
  }, [shouldShowPreferredCardModal]);

  const mapProps = {
    deviceWidth,
    isPrefCardModalOpen,
    handleDismiss,
    handleSubmit,
    shouldShowPreferredCardModal,
    ttpHeaderModalData,
    ...props,
  };

  return <TenderTypeModal {...mapProps} />;
}

TenderTypeModalVM.propTypes = {
  deviceWidth: PropTypes.string.isRequired,
  hideTopHeader: PropTypes.bool,
  topPosition: PropTypes.number,
  windowWidth: PropTypes.number,
};

TenderTypeModalVM.defaultProps = {
  hideTopHeader: false,
  topPosition: 0,
  windowWidth: 0,
};

export default TenderTypeModalVM;
